import { http } from '@api/client';
import { paramterize, pickResponse } from '@api/utils';
import {
  ApolloContactDetail,
  ApolloContactsQuery,
  ApolloEnrichedContact,
  ApolloSearchItem,
  ContactSearchResponse,
} from '@pages/apollo-enrichment-contact/apollo-enriched-contacts.types';
import { EMPTY } from 'rxjs';
import { expand, map, reduce } from 'rxjs/operators';

interface ApiEnvelope<T> {
  data: T;
  success: boolean;
}

interface ApolloSearchPayload {
  count: number;
  data: ApolloSearchItem[];
  next?: number | null;
  page?: number;
  page_size: number;
  prev?: number | null;
}

const text = (value?: string | null) => value || '';

const uniqueJoin = (values: string[]) =>
  Array.from(new Set(values.map(value => value.trim()).filter(Boolean))).join(', ');

const mapSearchItem = (record: ApolloSearchItem): ApolloEnrichedContact => ({
  id: record.id,
  personName: text(record.person_name),
  email: text(record.email),
  emailStatus: text(record.email_status),
  dbExists: record.db_exists === true,
  phone: '',
  designation: text(record.designation),
  companyName: text(record.company),
  linkedinUrl: '',
  location: text(record.location),
  modifiedAt: text(record.modified_at),
  groupedCount: 1,
  requests: [],
});

export const groupByPersonName = (contacts: ApolloEnrichedContact[]): ApolloEnrichedContact[] => {
  const order: string[] = [];
  const groups = new Map<string, ApolloEnrichedContact[]>();

  contacts.forEach(contact => {
    const key = contact.personName.trim().toLowerCase() || `id:${contact.id}`;
    if (!groups.has(key)) {
      order.push(key);
      groups.set(key, []);
    }
    groups.get(key).push(contact);
  });

  return order.map(key => {
    const group = groups.get(key);
    const primary = group[0];
    return {
      ...primary,
      companyName: uniqueJoin(group.map(item => item.companyName)),
      designation: uniqueJoin(group.map(item => item.designation)),
      email: uniqueJoin(group.map(item => item.email)),
      emailStatus: uniqueJoin(group.map(item => item.emailStatus)),
      dbExists: group.some(item => item.dbExists),
      location: uniqueJoin(group.map(item => item.location)),
      groupedCount: group.length,
    };
  });
};

const FETCH_PAGE_SIZE = 100;

const SORT_FIELD: Record<string, keyof ApolloEnrichedContact> = {
  id: 'id',
  person_name: 'personName',
  designation: 'designation',
  company: 'companyName',
  email: 'email',
  location: 'location',
};

const sortGrouped = (
  contacts: ApolloEnrichedContact[],
  sortBy: ApolloContactsQuery['sortBy'] = []
) => {
  if (!sortBy.length) {
    return contacts;
  }

  return [...contacts].sort((left, right) => {
    for (const { id, desc } of sortBy) {
      const field = SORT_FIELD[id] || (id as keyof ApolloEnrichedContact);
      const result = String(left[field] ?? '').localeCompare(String(right[field] ?? ''), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      if (result !== 0) {
        return desc ? -result : result;
      }
    }
    return 0;
  });
};

const searchApolloPage = (
  page: number,
  filtersMap: Record<string, string>,
  sort: string
) => {
  const params = paramterize({
    page_size: FETCH_PAGE_SIZE,
    page,
    sort,
    ...filtersMap,
  });

  return http
    .get<ApiEnvelope<ApolloSearchPayload>>('/person/apollo/searches', { params })
    .pipe(pickResponse());
};

export function getApolloContacts({
  filters = [],
  pageSize: page_size = 25,
  page = 1,
  sortBy = [],
}: ApolloContactsQuery = {}) {
  const sort = sortBy.map(({ id, desc }) => `${desc ? '$desc' : '$asc'}|||${id}`).join(';');
  const filtersMap: Record<string, string> = filters.reduce(
    (accumulator, filter) =>
      Object.assign(accumulator, {
        [filter.id]: Object.entries(filter.value)
          .map(([op, value]) =>
            op === '$bw' ? `$gte|||${value[0]};$lte|||${value[1]}` : `${op}|||${value}`
          )
          .join(';'),
      }),
    {}
  );

  return searchApolloPage(1, filtersMap, sort).pipe(
    expand(body => {
      const payload = body.data;
      const pageNumber = payload?.page || 1;
      const loaded = pageNumber * (payload?.page_size || FETCH_PAGE_SIZE);
      const nextPage =
        payload?.next && payload.next !== pageNumber ? payload.next : pageNumber + 1;
      const hasMore =
        Boolean(payload?.data?.length) &&
        pageNumber < 50 &&
        (Boolean(payload?.next && payload.next !== pageNumber) || (payload?.count || 0) > loaded);

      if (!hasMore) {
        return EMPTY;
      }

      return searchApolloPage(nextPage, filtersMap, sort);
    }),
    map(body => body.data?.data || []),
    reduce((all, pageRows) => all.concat(pageRows), [] as ApolloSearchItem[]),
    map(records => {
      const grouped = sortGrouped(groupByPersonName(records.map(mapSearchItem)), sortBy);
      const start = Math.max(page - 1, 0) * page_size;

      return {
        data: {
          count: grouped.length,
          page_size,
          data: grouped.slice(start, start + page_size),
        } as ContactSearchResponse,
      };
    })
  );
}

export function getApolloContact(id: number) {
  return http
    .get<ApiEnvelope<ApolloContactDetail>>(`/person/apollo/${id}`)
    .pipe(pickResponse());
}
